import { Alert, Box, CircularProgress, CssBaseline, Snackbar } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "components/hoc/Theme";
import { useCheckAuthMutation, useGetUserQuery } from "features/auth/auth";
import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import ClientViewLayout from "components/clientView/ClientViewLayout";
import LoginPage from "./containers/LoginPage";
import ResetPasswordPage from "./containers/ResetPasswordPage";
import ResetPasswordWithTokenPage from "./containers/ResetPasswordWithTokenPage";
import VerifyEmailPage from "./containers/VerifyEmailPage";
import AccountSettingsPage from "./containers/AccountSettingsPage";
import ClientViewAccountSettingsPage from "./containers/ClientViewAccountSettingsPage";
import AdminRoute from "components/common/AdminRoute";
import CalibrateAdminRoute from "components/common/CalibrateAdminRoute";
import ProtectedRoute from "components/common/ProtectedRoute";
import PublicRoute from "components/common/PublicRoute";
import ResetPasswordRoute from "components/common/ResetPasswordRoute";
import ChatPage from "containers/ChatPage";
import HomePage from "containers/HomePage";
import IntegrationsPage from "containers/IntegrationsPage";
import IntegrationDetailsPage from "containers/IntegrationDetailsPage";
import UserManagementPage from "containers/UserManagementPage";
import { useAppSelector, useAppDispatch } from "features/store";
import { clearNotification } from "features/notificationSlice";
import { logout } from "features/auth/authSlice";
import { localStorageUtils } from "utils/localStorage";
import { useTokenRefresh } from "hooks/useTokenRefresh";

function App() {
  const [checkAuth] = useCheckAuthMutation();
  const [authInProgress, setAuthInProgress] = useState(true);

  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const notification = useAppSelector((state) => state.notification);

  useTokenRefresh();
  const { isLoading: userLoading } = useGetUserQuery(undefined, {
    skip: !isAuthenticated,
  });

  useEffect(() => {
    if (window.location.pathname === "/login") {
      localStorageUtils.clearAppData();
      dispatch(logout());
      setAuthInProgress(false);
      return;
    }

    async function performAuthCheck() {
      try {
        await checkAuth().unwrap();
      } catch {
      } finally {
        setAuthInProgress(false);
      }
    }
    performAuthCheck();
  }, [checkAuth, dispatch]);

  if (authInProgress || userLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Snackbar
        open={!!notification.message}
        autoHideDuration={6000}
        onClose={() => dispatch(clearNotification())}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={notification.severity}
          onClose={() => dispatch(clearNotification())}
          sx={{ fontWeight: 600 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      <div style={{ backgroundColor: "white" }}>
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <ResetPasswordRoute>
                  <ResetPasswordPage />
                </ResetPasswordRoute>
              }
            />
            <Route
              path="/reset-password/:token"
              element={
                <PublicRoute>
                  <ResetPasswordWithTokenPage />
                </PublicRoute>
              }
            />
            <Route
              path="/verify-email/:token"
              element={<VerifyEmailPage />}
            />
            <Route
              path="/integrations"
              element={
                <AdminRoute>
                  <IntegrationsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/integrations/:id"
              element={
                <AdminRoute>
                  <IntegrationDetailsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/user-management"
              element={
                <CalibrateAdminRoute>
                  <UserManagementPage />
                </CalibrateAdminRoute>
              }
            />
            <Route
              path="/client-view/:userId"
              element={
                <CalibrateAdminRoute>
                  <ClientViewLayout />
                </CalibrateAdminRoute>
              }
            >
              <Route index element={<HomePage />} />
              <Route path="account-settings" element={<ClientViewAccountSettingsPage />} />
              <Route path="chat" element={<ChatPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="integrations/:id" element={<IntegrationDetailsPage />} />
            </Route>
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account-settings"
              element={
                <ProtectedRoute>
                  <AccountSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="*"
              element={
                isAuthenticated && user && !user.is_pass_reset ? (
                  <Navigate to="/" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
          </Routes>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
